import React, { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Inbox, UserCog, ShieldCheck, Wallet, BadgeDollarSign, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TableToolbar from "@/components/shared/TableToolbar";
import TablePagination from "@/components/shared/TablePagination";
import UserFormDialog from "@/components/admin/UserFormDialog";
import { adminUsersApi } from "@/lib/api/adminUsers";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const ROLE_ICON = {
    OPS_ADMIN: ShieldCheck,
    FINANCE_ADMIN: Wallet,
    SALES_AGENT: BadgeDollarSign,
};

function useDebounce(v, delay = 300) {
    const [d, setD] = useState(v);
    useEffect(() => { const t = setTimeout(() => setD(v), delay); return () => clearTimeout(t); }, [v, delay]);
    return d;
}

function CopyBadge({ value }) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    };
    return (
        <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-0.5 text-xs font-mono hover:bg-muted transition-colors"
            title="Copy referral code"
        >
            {value}
            {copied ? <Check className="h-3 w-3 text-success" /> : <Copy className="h-3 w-3 text-muted-foreground" />}
        </button>
    );
}

export default function UsersPage() {
    const { t } = useI18n();
    const { user: me } = useAuth();
    const [items, setItems] = useState(null);
    const [total, setTotal] = useState(0);
    const [query, setQuery] = useState("");
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [confirmDel, setConfirmDel] = useState(null);
    const [filterValues, setFilterValues] = useState({});
    const [sortValue, setSortValue] = useState("name_asc");
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);
    const debounced = useDebounce(query, 300);

    const load = useCallback(async () => {
        try {
            const params = {
                q: debounced || undefined,
                role: filterValues.role || undefined,
                is_active: filterValues.is_active || undefined,
                sort: sortValue || undefined,
                limit: pageSize,
                skip: (page - 1) * pageSize,
            };
            const res = await adminUsersApi.list(params);
            setItems(res.items);
            setTotal(res.total);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    }, [debounced, filterValues, sortValue, pageSize, page]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); }, [debounced, filterValues, sortValue, pageSize]);

    const onSaved = (saved) => {
        setItems((prev) => {
            const list = prev || [];
            const idx = list.findIndex((u) => u.id === saved.id);
            if (idx >= 0) {
                const next = [...list];
                next[idx] = saved;
                return next;
            }
            return [saved, ...list];
        });
        setTotal((p) => (items?.some((x) => x.id === saved.id) ? p : p + 1));
    };

    const onDelete = async () => {
        try {
            await adminUsersApi.remove(confirmDel.id);
            toast.success(t("admin.users.deleted"));
            setItems((prev) => prev?.filter((u) => u.id !== confirmDel.id));
            setTotal((p) => Math.max(0, p - 1));
            setConfirmDel(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
        }
    };

    return (
        <div className="max-w-7xl space-y-6 fade-up" data-testid="page-admin-users">
            <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t("admin.users.title")}</h1>
                    <p className="mt-2 text-sm text-muted-foreground max-w-2xl">{t("admin.users.subtitle")}</p>
                </div>
                <Button onClick={() => setDialog({ open: true, initial: null })} data-testid="add-user-button">
                    <Plus className="h-4 w-4 me-1" />
                    {t("admin.users.add")}
                </Button>
            </header>

            <TableToolbar
                search={query}
                onSearchChange={setQuery}
                searchPlaceholder={t("admin.users.searchPlaceholder")}
                filters={[
                    {
                        key: "role",
                        label: t("admin.users.cols.role"),
                        options: [
                            { value: "OPS_ADMIN", label: t("roles.OPS_ADMIN") },
                            { value: "FINANCE_ADMIN", label: t("roles.FINANCE_ADMIN") },
                            { value: "SALES_AGENT", label: t("roles.SALES_AGENT") },
                        ],
                    },
                    {
                        key: "is_active",
                        label: t("admin.users.cols.status"),
                        options: [
                            { value: "true", label: t("admin.users.active") },
                            { value: "false", label: t("admin.users.inactive") },
                        ],
                    },
                ]}
                filterValues={filterValues}
                onFilterChange={(k, v) => setFilterValues((p) => ({ ...p, [k]: v }))}
                onClearFilters={() => setFilterValues({})}
                sortOptions={[
                    { value: "name_asc", label: t("table.sort.nameAsc") },
                    { value: "name_desc", label: t("table.sort.nameDesc") },
                    { value: "created_desc", label: t("table.sort.createdDesc") },
                    { value: "created_asc", label: t("table.sort.createdAsc") },
                ]}
                sortValue={sortValue}
                onSortChange={setSortValue}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
            />

            {items === null && (
                <div className="surface-card p-12 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
            )}

            {items && items.length === 0 && (
                <div className="surface-card p-10 text-center" data-testid="users-empty">
                    <Inbox className="mx-auto h-8 w-8 text-muted-foreground mb-3" />
                    <p className="text-sm font-medium">{t("admin.users.empty")}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{t("admin.users.emptyHint")}</p>
                </div>
            )}

            {items && items.length > 0 && (
                <div className="surface-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="users-table">
                            <thead className="bg-muted/30">
                                <tr>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.users.cols.user")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.users.cols.role")}</th>
                                    <th className="text-start px-5 py-3 font-semibold">{t("admin.users.cols.status")}</th>
                                    <th className="px-5 py-3 font-semibold text-end">{t("admin.users.cols.actions")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((u) => {
                                    const Icon = ROLE_ICON[u.role] || UserCog;
                                    const isMe = me?.id === u.id;
                                    return (
                                        <tr key={u.id} className="border-t border-border hover:bg-muted/20" data-testid={`user-row-${u.id}`}>
                                            <td className="px-5 py-3">
                                                <div className="flex items-start gap-2.5">
                                                    <div className="icon-tile h-9 w-9 shrink-0">
                                                        <Icon className="h-4 w-4" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="font-semibold truncate">
                                                            {u.full_name}
                                                            {isMe ? <span className="text-xs text-muted-foreground ms-2">({t("admin.users.you")})</span> : null}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground font-mono truncate">{u.email}</div>
                                                        {u.username && (
                                                            <div className="text-xs text-muted-foreground font-mono truncate">@{u.username}</div>
                                                        )}
                                                        {u.referral_code && (
                                                            <div className="mt-1">
                                                                <CopyBadge value={u.referral_code} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-5 py-3">
                                                <Badge variant="outline">{t(`roles.${u.role}`)}</Badge>
                                            </td>
                                            <td className="px-5 py-3">
                                                {u.is_active ? (
                                                    <Badge variant="outline" className="border-success/40 text-success">{t("admin.users.active")}</Badge>
                                                ) : (
                                                    <Badge variant="secondary">{t("admin.users.inactive")}</Badge>
                                                )}
                                            </td>
                                            <td className="px-5 py-3 text-end">
                                                <div className="inline-flex gap-1">
                                                    <Button size="icon" variant="ghost" onClick={() => setDialog({ open: true, initial: u })} data-testid={`user-edit-${u.id}`} aria-label="Edit">
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="icon" variant="ghost" onClick={() => setConfirmDel(u)} disabled={isMe} data-testid={`user-delete-${u.id}`} aria-label="Delete">
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <TablePagination total={total} page={page} pageSize={pageSize} onPageChange={setPage} />
                </div>
            )}

            <UserFormDialog
                open={dialog.open}
                onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}
                initial={dialog.initial}
                onSaved={onSaved}
            />
            <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
                <AlertDialogContent data-testid="user-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t("admin.users.confirmDeleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t("admin.users.confirmDeleteBody", { name: confirmDel?.full_name || "" })}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={onDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="user-delete-confirm-button">
                            {t("admin.users.delete")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
